import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useToast } from 'vue-toast-notification'
import { config } from '@/config/env'
import { useLocaleStore } from '@/stores/localeStore'

export const useUserStore = defineStore('user', () => {
  const toast = useToast()

  const user = ref(null)
  const isAuthenticated = ref(false)
  const loading = ref(false)
  const error = ref(null)

  const resetAuthState = () => {
    user.value = null
    isAuthenticated.value = false
    error.value = null

    const localeStore = useLocaleStore()
    localeStore.restoreDefaultCountry()
  }

  const normalizeValue = (key, value) => {
    if (key === 'birthDate') return value?.split('T')[0]
    return value
  }

  const normalizeUser = (userData) => {
    if (!userData) return null

    if (userData.user === null) return null
    const normalized = userData.user ?? userData

    if (!normalized || typeof normalized !== 'object') return null

    return { ...normalized }
  }

  const hasChanges = (payload, currentUser) => {
    if (!currentUser) return true

    return Object.keys(payload).some((key) =>
      normalizeValue(key, payload[key]) !== normalizeValue(key, currentUser[key])
    )
  }

  const setUser = (userData) => {
    const normalizedUser = normalizeUser(userData)

    if (!normalizedUser) {
      resetAuthState()
      return null
    }

    user.value = normalizedUser
    isAuthenticated.value = true
    error.value = null

    const localeStore = useLocaleStore()
    localeStore.setFromUser(user.value)

    return user.value
  }

  const fetchUser = async () => {
    loading.value = true
    error.value = null

    try {
      const response = await fetch(config.apiUrl + '/user/whoami', {
        credentials: 'include'
      })

      if (!response.ok) {
        if (response.status === 401) {
          resetAuthState()
          return null
        }

        const data = await response.json().catch(() => ({}))
        throw new Error(data.message || data.error || 'Erreur lors de la récupération de l\'utilisateur')
      }

      const data = await response.json().catch(() => null)

      if (!data) {
        resetAuthState()
        return null
      }

      if (Object.prototype.hasOwnProperty.call(data, 'authenticated')) {
        if (!data.authenticated || !data.user) {
          resetAuthState()
          return null
        }

        return setUser(data.user)
      }

      const normalizedUser = normalizeUser(data)

      if (!normalizedUser) {
        resetAuthState()
        return null
      }

      return setUser(normalizedUser)
    } catch (err) {
      error.value = err.message
      user.value = null
      isAuthenticated.value = false
      console.error('Erreur fetchUser:', err)
      return null
    } finally {
      loading.value = false
    }
  }

  const updateUser = async (payload) => {
    loading.value = true
    error.value = null

    if (!user.value) {
      loading.value = false
      throw new Error('Aucun utilisateur connecté')
    }

    if (!hasChanges(payload, user.value)) {
      toast.info('Aucune modification détectée')
      loading.value = false
      return user.value
    }

    try {
      const response = await fetch(config.apiUrl + '/user/profile', {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Erreur lors de la mise à jour du profil')
      }

      const updatedUser = setUser(data)
      toast.success('Profil mis à jour avec succès')

      return updatedUser
    } catch (err) {
      error.value = err.message
      toast.error(`Échec de la mise à jour : ${err.message}`)
      throw err
    } finally {
      loading.value = false
    }
  }

  const updateAvatar = async (file) => {
    loading.value = true
    error.value = null

    try {
      const formData = new FormData()
      formData.append('avatar', file)

      const response = await fetch(config.apiUrl + '/user/avatar', {
        method: 'POST',
        credentials: 'include',
        body: formData
      })

      const data = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Erreur upload avatar')
      }

      return setUser(data)
    } catch (err) {
      error.value = err.message
      toast.error(`Échec de la mise à jour : ${err.message}`)
      throw err
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    try {
      const response = await fetch(config.apiUrl + '/user/logout', {
        method: 'POST',
        credentials: 'include'
      })

      if (!response.ok) {
        throw new Error('Erreur lors de la déconnexion')
      }

      resetAuthState()
      toast.success('Déconnecté')
    } catch (err) {
      console.error(err)
      toast.error(err.message)
    }
  }

  return {
    user,
    isAuthenticated,
    loading,
    error,
    setUser,
    fetchUser,
    updateUser,
    updateAvatar,
    logout,
    resetAuthState
  }
})
